// ┌──────────────────────────────────────────────┐
// │ Protection Plan Selector (E20-863 — POC)       │
// └──────────────────────────────────────────────┘
// When a plan is chosen and the customer adds to cart, the machine and the selected
// plan are added in a single AJAX call — both stamped with a shared `_bundle_id` (the
// definitive link), the plan also carrying `_parent_sku` — so the integration can link
// the pair downstream. The machine additionally carries `_has_assoc_protection_plan` and
// the plan `_protect_plan_for_assoc_product` (E20-1195) as fast relationship flags. When
// no plan is chosen, the theme's normal add-to-cart proceeds untouched.
//
// POC notes: `_parent_sku` is the server-rendered current variant SKU (pending the
// final SAP data-contract decision); it does not yet follow live variant changes.

class ProtectionPlanSelector extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.loading = false;
    this.select = this.querySelector('.product-protection-plan__select');
    this.parentSku = this.dataset.parentSku || '';
    this.form = this.closest('.product-section')?.querySelector('form[action*="/cart/add"]');

    if (!this.select || !this.form) return;

    // Capture phase so we run before — and can pre-empt — the theme's own submit handler.
    this.form.addEventListener('submit', this.onSubmit.bind(this), {
      capture: true,
      signal: this.controller.signal,
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  onSubmit(event) {
    // Only intercept the standard Add to Cart submit. Dynamic checkout buttons
    // (Shop Pay / "Buy it now") submit the same form — let those proceed untouched.
    const submitter = event.submitter;
    if (!submitter || !submitter.matches('[data-add-to-cart], .add-to-cart')) return;

    const planVariantId = this.select.value;
    if (!planVariantId) return; // no plan selected → let the theme add the machine normally

    // A bundle is only valid with a parent SKU to link the plan to its machine. If it's
    // missing (misconfigured product), don't form a broken bundle: let the theme add the
    // machine normally and skip the plan. This also avoids emitting an empty `_parent_sku`,
    // which the cart sync would misread as a machine line (it keys off `_parent_sku` presence).
    if (!this.parentSku) {
      console.warn('Protection plan: machine has no parent SKU; skipping bundle add.');
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.addBundle(planVariantId);
  }

  async addBundle(planVariantId) {
    if (this.loading) return; // in-flight guard: ignore rapid double-submits

    const formData = new FormData(this.form);
    const machineVariantId = formData.get('id');
    if (!machineVariantId) return; // returns before the guard is set — nothing to reset

    // Match the machine quantity (the qty input is form-associated) so the plan
    // count tracks the machine. Defaults to 1 when no quantity selector is present.
    const quantity = Number(formData.get('quantity')) || 1;

    const button = this.form.querySelector('.add-to-cart');
    this.loading = true;
    button?.classList.add('btn--loading');

    // Shared with the cart's own plan offer (window.bfxCart, bfx-cart-remove.js), so both
    // entry points mint the same shape of id.
    const bundleId = window.bfxCart.bundleId();

    // onSubmit only reaches addBundle with a non-empty parentSku, so the machine↔plan link is
    // valid here and _protect_plan_for_assoc_product is set. Guarding the link once, at the entry
    // point, keeps an orphaned plan (no matching machine) from ever being flagged as linked.
    // Flags are sent as the string 'true': Shopify persists line-item property values as strings,
    // so this matches what's stored and read back, rather than relying on the Cart API to coerce.
    const planProperties = {
      _bundle_id: bundleId,
      _parent_sku: this.parentSku,
      _protect_plan_for_assoc_product: 'true',
    };

    const payload = {
      items: [
        {
          id: Number(machineVariantId),
          quantity,
          // _has_assoc_protection_plan is only set here, where a plan was chosen alongside the
          // machine; the theme's plain add-to-cart omits it entirely — matching _bundle_id.
          // Sent as the string 'true' to match Shopify's string line-item-property storage.
          properties: { _bundle_id: bundleId, _has_assoc_protection_plan: 'true' },
        },
        {
          id: Number(planVariantId),
          quantity,
          properties: planProperties,
        },
      ],
    };

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok) {
        this.notifyError(data && data.description);
        console.error('Protection plan add failed:', data);
        return;
      }

      this.form.querySelector('.errors')?.remove();
      // Bubbles to document → Impulse rebuilds and opens the cart drawer.
      this.form.dispatchEvent(
        new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: data } })
      );
      this.select.value = '';
    } catch (error) {
      this.notifyError();
      console.error('Protection plan add error:', error);
    } finally {
      this.loading = false;
      button?.classList.remove('btn--loading');
    }
  }

  // Surface failures the way the theme's ProductForm does: render a visible
  // .errors message in the form AND dispatch ajaxProduct:error.
  notifyError(message) {
    const errorMessage =
      message || 'Could not add the protection plan to your cart. Please try again.';

    const existing = this.form.querySelector('.errors');
    if (existing) existing.remove();

    const errorDiv = document.createElement('div');
    errorDiv.classList.add('errors', 'text-center');
    errorDiv.textContent = errorMessage;
    this.form.append(errorDiv);

    this.form.dispatchEvent(
      new CustomEvent('ajaxProduct:error', { bubbles: true, detail: { errorMessage } })
    );
  }
}

// Guard against double-definition (re-evaluation under theme customization / bundling).
if (!customElements.get('protection-plan-selector')) {
  customElements.define('protection-plan-selector', ProtectionPlanSelector);
}
